/**
 * 이상한 방 - 1인 10단계 선택 화면 (순수 DOM)
 *
 * 근거 문서: 상태모델 9.5절(싱글의 포지션 1순위 = **튜토리얼**) · 5.4절(동사의 단계적 도입)
 *           컨셉시트 8절("한 판 더 하자는 말이 나오는가")
 *
 * ## 이 화면의 목적은 "선택"이 아니라 "다음 한 판"이다
 *
 * 싱글은 멀티의 준비 과정이므로(상태모델 9.5절), 이 화면이 해야 할 일은
 * 10개를 늘어놓고 고르게 하는 것이 아니라 **지금 해야 할 한 단계를 손가락 밑에 놓는 것**이다.
 * 튜토리얼에서 "뭘 골라야 하지"로 3초를 쓰면 그 3초는 멀티에 도달할 확률에서 빠진다.
 *
 * ## 목록으로 그리는 이유
 *
 * 10단계는 커리큘럼이고 순서가 곧 규칙이라, 격자로 접히는 순간 "다음에 뭘 해야 하는가"가
 * 읽는 문제가 된다. 세로 한 줄씩 내려오는 목록이면 순서를 설명할 문장이 아예 필요 없다.
 * 지난 단계는 기록만 남기고 물러나고, 지금 할 한 단계만 노란 번호판을 단 플레이트로 떠오른다.
 * 이 배치를 부르는 이름은 `levelSelect.css`에 있다(**현장 목록**. 2026-09-02까지는 편성표였다).
 *
 * 은유는 배치까지다. 줄에서 먼저 읽혀야 하는 것은 단계 번호와 제목, 잠겼는지, 기록이 얼마인지이고,
 * 그 위에 "1번 현장" 같은 은유 어휘를 덧씌우면 정보를 가리려고 은유를 쓴 꼴이 된다.
 *
 * ## 잠긴 단계를 지우지 않고 보여 주는 이유
 *
 * 5.4절의 도입 순서(제거 → 추가 → 이동 → 회전 → 색)는 그 자체가 커리큘럼이다.
 * 아래에 아직 배울 것이 남아 있다는 사실이 보여야 "이건 연습이다"가 전달되고,
 * 그래야 3단계에서 막혀도 게임이 끝난 것으로 읽히지 않는다.
 *
 * ## 이 화면에는 단계만 있다 (로드맵 M13)
 *
 * 한때 여기에 매일의 방 카드·여럿이 카드·계정 줄(핀 · 봉투 · 사물함)이 함께 서 있었다. 폰 세로
 * 375×812에서 **단계 목록에 남는 높이가 326px**이었다 - 열 단계 중 세 줄 반. 위 머리말이 스스로
 * 적어 둔 경고("요소 하나를 더할 때마다 그 목적과 경쟁한다")가 M3·M6·M9를 거치며 현실이 됐다.
 *
 * 셋은 전부 메뉴(`ui/menu.ts`)로 갔다. **다시 여기로 오지 않는다** - 그 판단의 근거는 아래 넷이고,
 * 넷 다 계정 탄창을 두지 않기로 했을 때 세운 것 그대로다:
 *
 *  1. **여기서는 값이 변하지 않는다.** 이 화면이 열려 있는 동안 절대 움직이지 않는 숫자는 계기판이
 *     아니라 **장식**이고, 장식은 이 화면이 가장 아끼는 것(다음 한 판)을 먹는다.
 *  2. **동사가 없는 숫자는 질문만 남긴다.** "핀 37"을 본 사람의 다음 생각은 "어디에 쓰지"인데
 *     봉투 입구가 이 화면에 없으면 그 답이 없다. 동사와 숫자는 같은 화면에 있어야 한다.
 *  3. 이 화면의 목적은 "다음 한 판을 손가락 밑에 놓는 것"이다(위).
 *  4. 실무적으로도 값이 싸지 않다 - 칩 하나에 `levelSelect.css`와 사전 양쪽을 건드려야 하고,
 *     인라인 스타일이나 한국어 하드코딩으로 우회하면 시각 언어와 번역 규칙을 동시에 깬다.
 *
 * 보유량이 **실제로 판단 재료가 되는 곳**에는 여전히 떠 있다: 메뉴의 서랍 줄, 수색 중 HUD의 탄창 칩
 * (`ui/gameHud.ts`), 개발 훅(`__weirdRoom.stats().accountMagazines`).
 *
 * ## `levels.ts` / `progress.ts`와의 결합
 *
 * 이 파일은 두 모듈을 **import 하지 않는다.** 대신 필요한 필드만 적은 구조적 계약
 * (`LevelSelectLevel`)을 받는다. 표시 계층이 저장 포맷을 알면 세이브 스키마가 바뀔 때마다
 * 화면이 같이 깨지고, 반대로 화면 사정으로 `LevelDef`에 표시용 필드가 늘어난다.
 * 저장 형태(Map·Record·배열)도 `bestOf` 조회 함수 하나로 숨긴다.
 *
 * ## 문구는 전부 `t()`를 지난다
 *
 * 하드코딩한 한국어를 남기지 않는다(`i18n/GLOSSARY.md`). 단계 이름과 힌트는 사전에 있으면
 * 사전이 이기고, 없으면 넘어온 값을 쓴다. 이유는 `levelText.ts`에 적었다.
 */

import { MS_PER_SECOND } from '../core/time'
import type { Verb } from '../core/types'
import { t } from '../i18n'

import { createElement, formatNumber, setBarRatio } from './dom'
import { formatClock } from './gameHud'
import * as levelText from './levelText'
import { attachScrollFade } from './scrollFade'
import { isSettingsOpen } from './settings'
import './levelSelect.css'

/* --- 표시 상수 ----------------------------------------------------------------- */

/**
 * 줄 오른쪽 표식 둘. 이모지를 쓰지 않는 이유는 플랫폼마다 크기와 여백이 달라 줄이 흔들리기 때문이다.
 * 상태를 색이 아니라 **글자**로도 말하므로 색각 이상에서도 그대로 읽힌다.
 */
const CLEARED_GLYPH = '✓'
const GO_GLYPH = '▸'

/**
 * 숫자키 → 단계 순서. 문자의 위치 + 1이 단계이고, 끝의 `0`이 10단계다(키보드 배열 그대로).
 *
 * 표로 적지 않는 이유는 10줄을 옮겨 적다 하나를 틀려도 아무도 눈치채지 못하기 때문이다.
 * 이 단축키는 플레이어 기능이 아니라 **개발 도구**다(상태모델 9.4절: 병목은 코딩이 아니라 반복 횟수).
 * 특정 단계의 난이도를 볼 때 매번 줄까지 커서를 옮기는 비용이 그대로 시행 횟수를 깎는다.
 */
const DIGIT_KEYS = '1234567890'

/* --- 입력 계약 ----------------------------------------------------------------- */

/**
 * 동사 목록의 두 가지 표현.
 *
 * `tuning.ts`는 배열(`readonly Verb[]`), `core/types.ts`의 `RoomConfig`는 집합(`ReadonlySet<Verb>`)을
 * 쓴다. 어느 쪽이 넘어오든 화면이 거절하지 않는 편이 낫다. 표시 계층이 자료구조 취향으로
 * 컴파일을 막는 것은 얻는 것이 없다.
 */
export type VerbList = readonly Verb[] | ReadonlySet<Verb>

/**
 * 이 화면이 한 단계에 대해 알아야 하는 전부. `levels.ts`의 `LevelSummary`가 이 필드들을
 * 같은 이름으로 갖고 있으면 **어댑터 없이 그대로 넘긴다**(구조적 타이핑).
 *
 * `objectCount`를 선택 필드로 둔 이유는 없으면 그 칩만 빠질 뿐 화면이 성립하기 때문이다.
 * 필수로 두면 넘기는 쪽이 그 값을 다르게 부르는 순간 화면 전체가 컴파일되지 않는다.
 */
export type LevelSelectLevel = Readonly<{
  /** 단계 식별자. `onSelect`로 그대로 되돌려 주고, 사전에서 이름을 찾는 열쇠이기도 하다. */
  id: number
  /** 사전에 `level.<id>.name`이 없을 때 쓰는 이름. `levelText.ts` 참고. */
  name: string
  /** 이 단계가 가르치는 것 한 문장. 선택 화면에서는 쓰지 않고 `levelIntro`가 쓴다. */
  hint: string
  changeCount: number
  huntSeconds: number
  allowedVerbs: VerbList
  /** 배치 목표 오브젝트 수. 밀도가 곧 이 게임의 난이도라 줄에 보일 값이다. */
  objectCount?: number
}>

/**
 * 한 단계의 최고 기록. `progress.ts`가 무엇을 저장하든 이 네 값으로 환산해서 넘긴다.
 * `changeCount`를 기록에 함께 담는 이유: 튜닝으로 단계의 이상한 곳 수가 바뀌어도
 * "12개 중 12개"라는 옛 기록이 거짓말이 되지 않아야 한다.
 *
 * **점수만 필수다.** 나머지 셋은 없을 수 있고, 그건 오류가 아니라 정상 입력이다 -
 * 클리어하지 못한 판은 최단 시간이 없고(`progress.ts`: 실패는 언제나 빠르다),
 * 그 두 필드가 생기기 전의 저장에는 발견 수가 없다. 없는 값을 0으로 채워 넘기면
 * 화면이 "0:00"이나 "0/0"이라는 **존재한 적 없는 기록**을 말하게 되므로,
 * 하나라도 비면 줄은 점수만 말한다(`statusLineFor`).
 */
export type LevelBest = Readonly<{
  score: number
  foundCount?: number | undefined
  changeCount?: number | undefined
  elapsedMs?: number | undefined
}>

/** 한 줄이 가질 수 있는 세 가지 얼굴. `current`는 항상 하나뿐이다. */
export type LevelCardState = 'cleared' | 'current' | 'locked'

/** 줄 하나를 그리는 데 필요한 전부. */
export type LevelCardView = Readonly<{
  level: LevelSelectLevel
  /** 화면상의 순서(1부터). `level.id`가 아니라 배열 순서다. 아래 `toLevelCards` 주석 참고. */
  order: number
  state: LevelCardState
  best: LevelBest | null
}>

/** `toLevelCards`가 진행도에서 읽는 전부. 두 가지뿐이다. */
export type LevelSelectSource = Readonly<{
  /** 앞에서부터 몇 단계를 깼는가. 0이면 1단계만 열려 있다. */
  clearedCount: number
  /** 단계별 최고 기록 조회. 없으면 모든 줄이 "아직 기록 없음"으로 그려진다. */
  bestOf?: (levelId: number) => LevelBest | null | undefined
}>

export type LevelSelectHandlers = Readonly<{
  /** 줄을 눌렀다. 잠긴 단계는 여기까지 오지 않는다(버튼이 `disabled`). */
  onSelect: (levelId: number) => void
  /** 메뉴로 돌아간다(로드맵 M13 - 뒤로는 언제나 메뉴다). 없으면 그 버튼은 서지 않는다. */
  onBack?: (() => void) | undefined
}>

export type LevelSelectHandle = Readonly<{
  /** 목록을 그린다. 이 화면이 그리는 것은 단계뿐이다(M13 규칙 2). */
  show: (cards: readonly LevelCardView[]) => void
  hide: () => void
  dispose: () => void
}>

export type LevelSelectOptions = Readonly<{
  mount: HTMLElement
  handlers: LevelSelectHandlers
}>

/* --- 변환 --------------------------------------------------------------------- */

function cardStateFor(order: number, clearedCount: number): LevelCardState {
  if (order <= clearedCount) return 'cleared'
  if (order === clearedCount + 1) return 'current'
  return 'locked'
}

/**
 * 단계 목록 + 진행도 → 줄 목록.
 *
 * 해금 계산에 `level.id`가 아니라 **배열 순서**를 쓴다. id가 1부터 시작한다는 가정을 화면이
 * 들고 있으면 `levels.ts`가 0-based가 되거나 id를 문자열스러운 값으로 바꾸는 순간
 * 전 단계가 잠긴 화면이 조용히 나온다. 순서는 배열이 이미 알고 있는 사실이다.
 */
export function toLevelCards(
  levels: readonly LevelSelectLevel[],
  source: LevelSelectSource,
): readonly LevelCardView[] {
  const clearedCount = Math.max(0, Math.floor(source.clearedCount))

  return levels.map((level, index) => {
    const order = index + 1
    return {
      level,
      order,
      state: cardStateFor(order, clearedCount),
      best: source.bestOf?.(level.id) ?? null,
    }
  })
}

/** 동사 목록을 배열로 정규화한다. 배열이든 집합이든 화면에서는 순서 있는 목록이다. */
export function toVerbArray(verbs: VerbList): readonly Verb[] {
  return Array.isArray(verbs) ? verbs : Array.from(verbs)
}

/* --- 단계 문구 ----------------------------------------------------------------- */

/** 단계 이름. 원본은 `levelText.ts`다. 여기 남긴 것은 `levelIntro`도 쓰는 `LevelSelectLevel` 모양의 래퍼다. */
export const levelNameOf = (level: LevelSelectLevel): string => levelText.levelNameOf(level.id, level.name)

/** 단계 힌트. 규칙은 이름과 같다. `levelIntro`의 유일한 산문이다. */
export const levelHintOf = (level: LevelSelectLevel): string => levelText.levelHintOf(level.id, level.hint)

/* --- 표시 서식 ----------------------------------------------------------------- */

/** 제한시간. 초 단위 설정값을 `M:SS`로. HUD 시계와 같은 표기여야 예습이 된다. */
function formatLimit(seconds: number): string {
  // `formatClock`이 ms를 받는다. 제한시간은 초로 오므로 여기서 한 번 곱한다.
  return formatClock(seconds * MS_PER_SECOND)
}

/* --- 줄 문구 ------------------------------------------------------------------- */

/**
 * 줄 아래 한 줄. 상태마다 다른 말을 한다.
 *
 * 잠긴 줄에까지 문구를 주는 이유: "왜 안 눌리는가"를 화면이 말하지 않으면
 * 눌리지 않는 버튼은 고장 난 버튼과 구별되지 않는다.
 */
function statusLineFor(card: LevelCardView): string {
  if (card.state === 'locked') return t('select.locked', { step: card.order - 1 })

  const best = card.best
  if (best === null) return card.state === 'current' ? t('select.startHere') : t('select.noRecord')

  /*
   * 셋 중 하나라도 없으면 점수만 말한다. 없는 값을 0으로 메우면 "0/0"이나 "0:00"이 되는데,
   * 그건 정보가 아니라 **거짓 기록**이고 한 줄짜리 기록표에서 거짓말은 표 전체를 못 믿게 만든다.
   * 어느 경우에 비는지는 `LevelBest` 주석에 있다(옛 저장 · 아직 못 깬 단계).
   */
  const { foundCount, changeCount, elapsedMs } = best
  if (foundCount === undefined || changeCount === undefined || elapsedMs === undefined) {
    return t('select.bestScoreOnly', { score: formatNumber(best.score) })
  }

  return t('select.best', {
    score: formatNumber(best.score),
    found: foundCount,
    total: changeCount,
    time: formatClock(elapsedMs),
  })
}

/** 스크린리더용 한 줄. 노란 번호판과 오른쪽 표식은 소리로 전달되지 않는다. */
function ariaLabelFor(card: LevelCardView): string {
  const stateKey =
    card.state === 'cleared'
      ? 'select.stateCleared'
      : card.state === 'current'
        ? 'select.stateCurrent'
        : 'select.stateLocked'

  return t('select.cardAria', {
    step: card.order,
    name: levelNameOf(card.level),
    state: t(stateKey),
    status: statusLineFor(card),
  })
}

/** 규칙 칩 하나. `label`이 있으면 화면 글자와 읽어 주는 말이 다르다는 뜻이다. */
type MetaChip = Readonly<{ text: string; label?: string }>

/**
 * 규칙 칩 목록. 값이 없는 칩은 아예 만들지 않는다. 빈 칩은 정보가 아니라 잡음이다.
 *
 * 칩을 요소로 나눠 두는 덕에 구분자 글자가 필요 없다(용어집이 가운뎃점 구분자를 금지한다).
 *
 * 제한시간만 화면에서 숫자 하나로 줄인다. "제한시간 1:00"을 그대로 쓰면 영어에서
 * ("1:00 on the clock") 칩이 줄바꿈되어 줄 높이가 한 단 늘어나고, 한 화면에 들어오는
 * 단계 수가 줄어든다. `M:SS` 꼴은 목록에서 이미 시간으로 읽히므로 라벨은 소리로만 남긴다.
 */
function metaChipsFor(level: LevelSelectLevel): readonly MetaChip[] {
  // 순서가 곧 난이도를 읽는 순서다. 찾을 것이 몇 개인가, 그게 얼마나 많은 물건 속에 있는가, 시간은.
  const chips: MetaChip[] = [{ text: t('select.chipOddities', { count: level.changeCount }) }]
  if (level.objectCount !== undefined) {
    chips.push({ text: t('select.chipObjects', { count: level.objectCount }) })
  }

  const time = formatLimit(level.huntSeconds)
  chips.push({ text: time, label: t('select.chipTimeAria', { time }) })
  return chips
}

/* --- 줄 조립 ------------------------------------------------------------------- */

function createRow(card: LevelCardView, onSelect: (levelId: number) => void): HTMLLIElement {
  const step = createElement('li', `ls-step is-${card.state}`)

  const row = createElement('button', 'ls-row')
  row.type = 'button'
  row.setAttribute('aria-label', ariaLabelFor(card))
  // 잠긴 단계는 `disabled`로 막는다. 포커스 순서에서도 빠져야 탭 이동이 벽에 부딪히지 않는다.
  row.disabled = card.state === 'locked'

  /*
   * 단계 번호는 상태와 무관하게 늘 보인다. 깼다고 번호를 체크 표시로 갈아 끼우면
   * 목록에서 번호가 사라지는 셈이고, "3단계를 깨면 열린다"를 읽은 눈이 3번 줄을 못 찾는다.
   */
  const no = createElement('span', 'ls-row__no', String(card.order))
  no.setAttribute('aria-hidden', 'true')

  const body = createElement('span', 'ls-row__body')
  body.append(createElement('span', 'ls-row__name', levelNameOf(card.level)))

  const meta = createElement('span', 'ls-row__meta')
  for (const chip of metaChipsFor(card.level)) {
    const node = createElement('span', 'ls-chip', chip.text)
    /*
     * 줄 전체의 `aria-label`이 있으므로 지금은 이 라벨이 읽히지 않는다.
     * 그래도 붙이는 이유는 숫자 하나짜리 칩을 이름 없는 채로 두지 않기 위해서다.
     */
    if (chip.label !== undefined) node.setAttribute('aria-label', chip.label)
    meta.append(node)
  }
  body.append(meta, createElement('span', 'ls-row__status', statusLineFor(card)))

  // 잠긴 줄에서는 CSS가 이 표식을 숨긴다. 글자를 비우지 않는 이유는 줄 끝 폭을 지키기 위해서다.
  const go = createElement('span', 'ls-row__go', card.state === 'cleared' ? CLEARED_GLYPH : GO_GLYPH)
  go.setAttribute('aria-hidden', 'true')

  row.append(no, body, go)
  row.addEventListener('click', () => onSelect(card.level.id))
  step.append(row)

  return step
}

/* --- 본체 ---------------------------------------------------------------------- */

/**
 * 단계 선택 화면을 만들어 `mount`에 붙인다. 처음에는 숨겨져 있다.
 * 언제 띄울지는 앱 상태머신의 몫이고, 이 화면은 자기가 먼저 나서지 않는다.
 */
export function createLevelSelect(options: LevelSelectOptions): LevelSelectHandle {
  const root = createElement('div', 'ls-root')
  root.hidden = true
  root.setAttribute('role', 'dialog')
  root.setAttribute('aria-modal', 'true')
  root.setAttribute('aria-labelledby', 'ls-title')

  const head = createElement('header', 'ls-head')
  const title = createElement('h1', 'ls-title', t('select.title'))
  title.id = 'ls-title'
  head.append(createElement('p', 'ls-eyebrow', t('select.eyebrow')), title)

  const progress = createElement('div', 'ls-progress')
  const track = createElement('div', 'ls-progress__track')
  const fill = createElement('div', 'ls-progress__fill')
  track.append(fill)
  const progressText = createElement('span', 'ls-progress__text')
  progress.append(track, progressText)
  head.append(progress)

  const scroll = createElement('div', 'ls-scroll')
  const list = createElement('ul', 'ls-list')
  scroll.append(list)

  const foot = createElement('p', 'ls-foot')

  /*
   * 메뉴로 돌아가는 줄(로드맵 M13). 꼬리말 아래, 판의 맨 끝이다 - 이 화면에서 가장 덜 중요한 동작이라
   * 위에 두면 열 단계보다 먼저 읽힌다. 핸들러가 없으면 만들지 않는다(감추는 것이 아니라 만들지 않는다).
   */
  const back = options.handlers.onBack === undefined ? null : createBackButton(options.handlers.onBack)

  root.append(head, scroll, foot)
  if (back !== null) root.append(back)
  options.mount.append(root)

  /*
   * 스크롤 끝 신호(`scrollFade.ts`). **이 화면에서는 없으면 안 되는 것이다** -
   * 터치 기기는 스크롤바를 숨기고(`gameHud.css`) 목록은 10단계 중 6~7줄만 보인다.
   * 신호가 없으면 사람이 나머지 단계를 아예 못 찾는다.
   */
  const stopScrollFade = attachScrollFade(scroll)

  // 숫자키 단축키가 참조할 현재 목록. 도메인 상태가 아니라 이 위젯이 마지막으로 그린 것이다.
  let cards: readonly LevelCardView[] = []

  function setProgress(clearedCount: number, total: number): void {
    progressText.textContent = t('select.progress', { cleared: clearedCount, total })
    /*
     * 다 깬 뒤에도 "다음이 열린다"를 계속 말하면 열릴 것이 없는데 기다리라는 말이 된다.
     * 10단계의 목적은 어려운 판을 이기는 것이 아니라 **멀티 규칙을 다 익힌 상태로 내보내는 것**이므로
     * (상태모델 9.5절), 마지막 화면이 가리켜야 할 곳은 다음 단계가 아니라 멀티다.
     */
    const graduated = total > 0 && clearedCount >= total
    foot.textContent = graduated ? t('select.footGraduated') : t('select.footInProgress')

    setBarRatio(fill, total > 0 ? clearedCount / total : 0)
  }

  /**
   * 숫자키로 단계 진입. 잠긴 단계는 무시한다(경고도 내지 않는다. 오타에 벌을 줄 이유가 없다).
   */
  function handleKeyDown(event: KeyboardEvent): void {
    if (root.hidden) return
    // 설정 패널이 위에 떠 있으면 키는 그쪽의 것이다. 안 비키면 패널에서 숫자를 누르는 순간
    // 뒤에서 단계가 시작되고, 패널은 사라진 화면 위에 남는다.
    if (isSettingsOpen()) return
    if (event.altKey || event.ctrlKey || event.metaKey) return

    const digit = DIGIT_KEYS.indexOf(event.key)
    if (digit < 0) return

    const card = cards.find((candidate) => candidate.order === digit + 1)
    if (card === undefined || card.state === 'locked') return

    event.preventDefault()
    options.handlers.onSelect(card.level.id)
  }

  window.addEventListener('keydown', handleKeyDown)

  return {
    show(next: readonly LevelCardView[]): void {
      cards = next
      // 열 줄뿐이라 매번 다시 만든다. 줄을 재사용해 아끼는 프레임보다
      // "상태가 갱신되지 않은 줄이 하나 남는" 버그의 비용이 훨씬 크다.
      list.replaceChildren()
      for (const card of next) {
        list.append(createRow(card, options.handlers.onSelect))
      }

      setProgress(next.filter((card) => card.state === 'cleared').length, next.length)
      root.hidden = false

      /*
       * 지금 할 단계에 포커스를 올려 둔다. 키보드로는 Enter 한 번, 폰에서는 그 줄이 화면에 들어온다.
       * 전 단계를 다 깨면 "지금 할 단계"가 없는데, 그때 아무 데도 포커스를 안 주면
       * `aria-modal` 대화상자를 열어 놓고 포커스는 바깥에 둔 꼴이 된다. 그래서 첫 줄로 떨어진다.
       */
      const current =
        list.querySelector<HTMLButtonElement>('.ls-step.is-current .ls-row') ??
        list.querySelector<HTMLButtonElement>('.ls-row:not(:disabled)')
      if (current === null) return

      current.focus()
      // `scrollIntoView`는 jsdom과 일부 구형 WebView에 없다. 없으면 `focus()`가 이미 대신 스크롤하므로
      // 목록 가운데로 맞추지 못할 뿐 화면은 성립한다. 여기서 던지면 선택 화면 전체가 안 뜬다.
      if (typeof current.scrollIntoView === 'function') current.scrollIntoView({ block: 'center' })
    },

    hide(): void {
      // 숨긴 화면 안에 포커스가 남으면 키보드 탐색이 보이지 않는 버튼을 계속 밟는다.
      if (root.contains(document.activeElement)) {
        const focused = document.activeElement
        if (focused instanceof HTMLElement) focused.blur()
      }
      root.hidden = true
    },

    dispose(): void {
      stopScrollFade()
      window.removeEventListener('keydown', handleKeyDown)
      root.remove()
    },
  }
}

/* --- 뒤로 -------------------------------------------------------------------- */

/**
 * 메뉴로 돌아가는 버튼(로드맵 M13). 물성은 `ui/menu.css`의 `.mn-back`과 같아야 하는데, 그 파일을
 * 여기서 import 하면 단계 선택이 메뉴의 CSS에 기대게 된다 - 그래서 이름만 같은 규칙을 이 화면의
 * 파일(`levelSelect.css`)에 둔다. 둘이 갈리면 뒤로 가는 버튼이 화면마다 다른 물건이 된다.
 */
function createBackButton(onBack: () => void): HTMLButtonElement {
  const back = createElement('button', 'ls-back', t('menu.back')) as HTMLButtonElement
  back.type = 'button'
  back.addEventListener('click', onBack)
  return back
}
